import { NavLink, useNavigate, useLocation } from "react-router-dom"
import { LayoutDashboard, Users, Droplets, Building2, Gauge } from "lucide-react"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuth } from "@/hooks/useAuth"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import useCurrentUser from "@/hooks/use-current-user"
import { hasReadPermission } from "@/lib/admin-permissions"

export function AdminSidebar() {
  const { state } = useSidebar()
  const collapsed = state === "collapsed"
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { hasRole } = useCurrentUser()
  const permissions = user?.permissions ?? []
  const canRead = (object: Parameters<typeof hasReadPermission>[1]) =>
    hasRole("super_admin") || hasReadPermission(permissions, object)

  const handleLogout = () => {
    logout()
    navigate("/login")
  }

  return (
    <Sidebar className={collapsed ? "w-16" : "w-64"}>
      <SidebarHeader className="border-b border-sidebar-border p-4">
        {!collapsed && (
          <>
            <div className="flex flex-col items-center justify-center gap-3 mb-4">
              <img src="/main-logo.jpg" alt="Water Bureau" className="h-24 w-32" />
              <div>
                {/* <h2 className="text-lg font-semibold text-primary">Water Bureau</h2> */}
                <p className="text-xs text-muted-foreground">Admin Portal</p>
              </div>
            </div>
            <div className="text-sm mb-4">
              <p className="font-medium">
                {hasRole("super_admin") ? "Nucleus3" : (user?.councils?.[0]?.name ?? "Unknown")}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Avatar className="size-6">
                  <AvatarFallback className="text-xs">
                    {user?.name
                      ?.split(" ")
                      .map((n: string) => n[0])
                      .join("") || "AU"}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-muted-foreground">{user?.name}</span>
              </div>
            </div>
          </>
        )}
      </SidebarHeader>

      <SidebarContent className="p-2">
        <SidebarGroup>
          <SidebarMenu>
            {canRead("dashboard") && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/"}>
                  <NavLink to="/" end>
                    <LayoutDashboard className="size-4" />
                    {!collapsed && <span>Dashboard</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {canRead("admin_management") && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/manage-admin"}>
                  <NavLink to="/manage-admin">
                    <Users className="size-4" />
                    {!collapsed && <span>Manage Admin</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {canRead("water_bureaus") && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/manage-waterbureau"}>
                  <NavLink to="/manage-waterbureau">
                    <Building2 className="size-4" />
                    {!collapsed && <span>Water Bureaus</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {canRead("devices") && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/meter-info"}>
                  <NavLink to="/meter-info">
                    <Gauge className="size-4" />
                    {!collapsed && <span>Meter Info</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {hasRole("council_admin") && (
              <>
                {canRead("devices") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location.pathname === "/devices"}>
                      <NavLink to="/devices">
                        <Droplets className="size-4" />
                        {!collapsed && <span>Devices</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {canRead("customers") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location.pathname === "/customers"}>
                      <NavLink to="/customers">
                        <Users className="size-4" />
                        {!collapsed && <span>Customers</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </>
            )}
          </SidebarMenu>
        </SidebarGroup>

        <div className="mt-auto p-2">
          <Button variant="outline" size="sm" onClick={handleLogout} className="w-full">
            {collapsed ? "Exit" : "Sign Out"}
          </Button>
        </div>
      </SidebarContent>
    </Sidebar>
  )
}
